import type { Identifier } from "ra-core";

import type { AdvisorAvailabilityBlock } from "../types";

/** Zone every block is read in (matches the assignment engine). */
export const AVAILABILITY_TIME_ZONE = "America/Mexico_City";

/** ISO weekdays, Monday first. */
export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

export type BlockValidationError =
  | "required"
  | "end_not_after_start"
  | "overlap";

export interface BlockDraft {
  weekday: number;
  start_time: string;
  end_time: string;
}

/** "HH:MM" or "HH:MM:SS" to minutes since midnight; NaN when malformed. */
export const timeToMinutes = (time: string): number => {
  const match = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(time);
  if (!match) return Number.NaN;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours > 23 || minutes > 59 ? Number.NaN : hours * 60 + minutes;
};

/** Trims a DB time ("09:00:00") to the "HH:MM" shown on screen. */
export const formatBlockTime = (time: string): string => time.slice(0, 5);

/** A block still counts while it has not been ended. */
export const isActiveBlock = (
  block: Pick<AdvisorAvailabilityBlock, "ended_at">,
) => !block.ended_at;

/**
 * Validates a new block against the advisor's own blocks. Returns an error code (mapped to a
 * plain Spanish message by the screen) or null when the block can be saved. Only ACTIVE
 * blocks on the same weekday are compared; touching edges (18:00 / 18:00) do not overlap.
 */
export const validateBlockDraft = (
  draft: BlockDraft,
  existing: readonly Pick<
    AdvisorAvailabilityBlock,
    "weekday" | "start_time" | "end_time" | "ended_at"
  >[],
): BlockValidationError | null => {
  const start = timeToMinutes(draft.start_time);
  const end = timeToMinutes(draft.end_time);
  if (!Number.isFinite(start) || !Number.isFinite(end) || !draft.weekday) {
    return "required";
  }
  if (end <= start) return "end_not_after_start";
  const overlaps = existing.some(
    (block) =>
      isActiveBlock(block) &&
      block.weekday === draft.weekday &&
      start < timeToMinutes(block.end_time) &&
      timeToMinutes(block.start_time) < end,
  );
  return overlaps ? "overlap" : null;
};

/** Active blocks of one advisor, ordered by weekday then start time. */
export const ownActiveBlocks = (
  blocks: readonly AdvisorAvailabilityBlock[],
  salesId: Identifier,
): AdvisorAvailabilityBlock[] =>
  blocks
    .filter((block) => String(block.sales_id) === String(salesId))
    .filter(isActiveBlock)
    .sort(
      (a, b) =>
        a.weekday - b.weekday ||
        timeToMinutes(a.start_time) - timeToMinutes(b.start_time),
    );
